import { useEffect, useState, useCallback, type Dispatch, type SetStateAction } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Card, Button, Cluster, Input, Select, Stack, EmptyState, useToast } from "@salesapp/ui";
import { crmApi, type Activity, type CommercialAgreement, type CommercialAgreementLine, type Company, type CompanyDashboard, type CompanyInput, type Contact, type Lead, type Opportunity, type Order, type Product, type Quote, type TimelineEvent } from "../../lib/crmApi";
import { ACTIVITY_OUTCOMES, COMPANY_STATUS_OPTIONS as COMPANY_STATUS_HINTS, FIELD_HINTS, LEAD_STATUS_OPTIONS, LEAD_TEMPERATURE_OPTIONS, statusTitle } from "../../help/fieldHints";
import { activityTypesHint, labelForActivityType } from "../../lib/activityTypes";
import { useLoggedActivityTypes } from "../../lib/useActivityTypes";
import { opsApi, type AppLayout, type FieldDef, type Invoice, type Territory } from "../../lib/opsApi";
import { pickerPage } from "../../lib/paging";
import { reportSaveFailure } from "../../lib/saveNotice";
import { useAccess } from "../../shell/AccessProvider";
import { DetailSection, FieldItem, RecordWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { StatusBadge, statusText } from "./StatusBadge";
import { RecordOwner } from "./RecordOwner";
import { FieldLabel } from "./FieldLabel";
import { CompanyDeleteButton } from "./CompanyDeleteButton";
import { DesignedRecordFields } from "./DesignedRecordFields";
import { withNormalizedLayout } from "./recordLayout";
import { LeadCaptureForm, leadDisplayName } from "./LeadCaptureForm";
import { ContactDuplicateWarning, RecordAuditInfo, useContactDuplicates } from "./RecordAuditInfo";
import { CompanyLookup } from "./CompanyLookup";
import { SearchBar, optionsList, statusLabel } from "./SearchBar";
import { useCrmHref } from "../../lib/customerUrl";
import { recordLink } from "./activityLinks";
import { useLocale } from "../../shell/LocaleProvider";
import { useColumnResize } from "./useColumnResize";
import { ResponsiveDataTable, type DataColumn } from "./ResponsiveDataTable";
import { ContactViewCard, contactName } from "./ContactViewCard";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";

type Tab = "details" | "contacts" | "activities" | "leads" | "deals" | "quotes" | "orders" | "invoices" | "agreements";

const READONLY_COMPANY_KEYS = new Set([
  "company_code",
  "id",
  "created_at",
  "updated_at",
  "territory_code",
  "territory_name",
]);

const COMPANY_STATUS_OPTIONS = ["active", "prospect", "incomplete", "dormant", "inactive"];

function companyStatusChoices(options: unknown, current: string | null | undefined): string[] {
  const raw = optionsList(options, COMPANY_STATUS_OPTIONS).filter((item) => item && item !== "merged" && item !== "churned");
  const unique = [...new Set(raw.length ? raw : COMPANY_STATUS_OPTIONS)];
  if (!unique.includes("inactive")) unique.push("inactive");
  if (current && current !== "merged" && !unique.includes(current)) unique.push(current);
  return unique;
}

const EMPTY_CONTACT = {
  first_name: "",
  last_name: "",
  work_email: "",
  personal_email: "",
  mobile: "",
  marketing_enabled: false,
  is_active: true,
};

const EMPTY_ACTIVITY = {
  activity_type: "cold_call",
  subject: "",
  body: "",
  outcome: "",
  contact_id: "",
};

const EMPTY_AGREEMENT = {
  status: "draft",
  start_date: "",
  end_date: "",
  payment_terms: "",
  delivery_terms: "",
  notes: "",
  lines: [] as CommercialAgreementLine[],
};

function formFromCompany(company: Company, fallbackTerritory: string): Record<string, string> {
  const next: Record<string, string> = {
    legal_name: company.legal_name ?? "",
    trading_name: company.trading_name ?? "",
    industry: company.industry ?? "",
    website: company.website ?? "",
    primary_phone: company.primary_phone ?? "",
    primary_email: company.primary_email ?? "",
    registration_number: company.registration_number ?? "",
    vat_number: company.vat_number ?? "",
    currency: company.currency ?? "",
    payment_terms: company.payment_terms ?? "",
    billing_address_line1: company.billing_address_line1 ?? "",
    billing_address_city: company.billing_address_city ?? "",
    billing_address_state: company.billing_address_state ?? "",
    billing_address_postal: company.billing_address_postal ?? "",
    billing_address_country: company.billing_address_country ?? "",
    notes: company.notes ?? "",
    account_status: company.account_status ?? "prospect",
    territory_id: company.territory_id ?? fallbackTerritory,
  };
  if (company.custom_fields) {
    for (const [key, value] of Object.entries(company.custom_fields)) {
      if (value == null) continue;
      next[key] = typeof value === "boolean" ? (value ? "true" : "false") : String(value);
    }
  }
  return next;
}

function companyPatch(form: Record<string, string>, includeTerritory: boolean): Partial<CompanyInput> {
  const blank = (key: string) => form[key] || null;
  return {
    legal_name: form.legal_name,
    trading_name: blank("trading_name"),
    industry: blank("industry"),
    website: blank("website"),
    primary_phone: blank("primary_phone"),
    primary_email: blank("primary_email"),
    registration_number: blank("registration_number"),
    vat_number: blank("vat_number"),
    currency: blank("currency"),
    payment_terms: blank("payment_terms"),
    billing_address_line1: blank("billing_address_line1"),
    billing_address_city: blank("billing_address_city"),
    billing_address_state: blank("billing_address_state"),
    billing_address_postal: blank("billing_address_postal"),
    billing_address_country: blank("billing_address_country"),
    notes: blank("notes"),
    account_status: form.account_status || null,
    ...(includeTerritory ? { territory_id: form.territory_id || null } : {}),
  };
}

function CompanyFieldControl({
  fieldKey,
  label,
  field,
  formatted,
  form,
  setForm,
  territories,
  canAssignTerritory,
}: {
  fieldKey: string;
  label: string;
  field?: FieldDef;
  formatted: string;
  form: Record<string, string>;
  setForm: Dispatch<SetStateAction<Record<string, string>>>;
  territories: Territory[];
  canAssignTerritory: boolean;
}) {
  const storeKey = fieldKey === "territory_name" ? "territory_id" : fieldKey;
  const value = form[storeKey] ?? "";
  function setValue(next: string) {
    setForm((current) => ({ ...current, [storeKey]: next }));
  }

  if (READONLY_COMPANY_KEYS.has(fieldKey)) return formatted;

  if (storeKey === "territory_id") {
    if (!canAssignTerritory || territories.length === 0) return formatted;
    return (
      <Select aria-label={label} value={value} onChange={(event) => setValue(event.target.value)}>
        {territories.map((territory) => (
          <option key={territory.id} value={territory.id}>{territory.name}</option>
        ))}
      </Select>
    );
  }

  if (fieldKey === "account_status" || field?.field_type === "select") {
    const options = fieldKey === "account_status"
      ? optionsList(field?.options, COMPANY_STATUS_OPTIONS)
      : optionsList(field?.options, []);
    return (
      <Select aria-label={label} value={value} onChange={(event) => setValue(event.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </Select>
    );
  }

  if (field?.field_type === "boolean") {
    return (
      <label className="crm-check">
        <input
          type="checkbox"
          checked={value === "true"}
          onChange={(event) => setValue(event.target.checked ? "true" : "false")}
        />
        {label}
      </label>
    );
  }

  return (
    <Input
      aria-label={label}
      type={field?.field_type === "email" ? "email" : field?.field_type === "phone" ? "tel" : "text"}
      value={value}
      onChange={(event) => setValue(event.target.value)}
    />
  );
}

function formatAddress(company: Company): string | undefined {
  const parts = [
    company.billing_address_line1,
    company.billing_address_city,
    company.billing_address_state,
    company.billing_address_postal,
    company.billing_address_country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : undefined;
}

function timelineTypeLabel(event: TimelineEvent): string {
  return (event.event_type || "event").replace(/_/g, " ");
}

function timelineTaskType(event: TimelineEvent): string {
  if (event.source === "activity") return "Activity";
  const type = event.event_type ?? "";
  if (type.endsWith("_created")) return "Created";
  if (type.endsWith("_updated") || type.endsWith("_changed")) return "Updated";
  if (type.endsWith("_deleted")) return "Deleted";
  if (type.includes("converted")) return "Converted";
  if (type.includes("reverted")) return "Reverted";
  if (type.includes("submitted")) return "Submitted";
  if (type.includes("approved")) return "Approved";
  if (type.includes("rejected")) return "Rejected";
  if (type.includes("sent")) return "Sent";
  if (type.includes("accepted")) return "Accepted";
  if (type.includes("generated")) return "Generated";
  return timelineTypeLabel(event);
}

function timelineTouchPoint(event: TimelineEvent): string {
  if (event.source === "activity") return "Activity";
  const type = event.event_type ?? "";
  const record = event.related_record_type ?? "";
  if (type.startsWith("lead_") || record.includes("leads")) return "Lead";
  if (type.startsWith("contact_") || record.includes("contacts")) return "Contact";
  if (type.startsWith("opportunity_") || record.includes("opportunities")) return "Opportunity";
  if (type.startsWith("agreement_") || record.includes("agreement")) return "Agreement";
  if (type.startsWith("quote_") || record.includes("quotes")) return "Quote";
  if (type.startsWith("order_") || record.includes("orders")) return "Order";
  if (type.startsWith("invoice_") || record.includes("invoices")) return "Invoice";
  if (type.startsWith("support_") || record.includes("support")) return "Support";
  if (type.startsWith("sales_cycle_") || record.includes("sales_cycle")) return "Sales cycle";
  if (type.startsWith("company_") || record.includes("companies")) return "Company";
  return "Company";
}

function timelineDiffs(event: TimelineEvent): Array<{ field_label?: string | null; old_value?: string | null; new_value?: string | null }> {
  const raw = event.metadata?.diffs;
  return Array.isArray(raw) ? raw as Array<{ field_label?: string | null; old_value?: string | null; new_value?: string | null }> : [];
}

function timelineBefore(event: TimelineEvent): string {
  const diffs = timelineDiffs(event);
  if (!diffs.length) return "";
  return diffs.map((diff) => `${diff.field_label ?? "Field"}: ${diff.old_value ?? "blank"}`).join("; ");
}

function timelineAfter(event: TimelineEvent): string {
  const diffs = timelineDiffs(event);
  if (!diffs.length) return "";
  return diffs.map((diff) => `${diff.field_label ?? "Field"}: ${diff.new_value ?? "blank"}`).join("; ");
}

function isEditableActivity(event: TimelineEvent): boolean {
  return event.source === "activity" && ["call", "email", "meeting", "note"].includes(event.event_type ?? "");
}

export function CompanyDetailPage({ orgId }: { orgId: string }) {
  const { companyId } = useParams<{ companyId: string }>();
  const { can } = useAccess();
  const { show } = useToast();
  const navigate = useNavigate();
  const crmHref = useCrmHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatMoney, formatDate } = useLocale();
  const activityTypes = useLoggedActivityTypes(orgId);
  const activityHint = activityTypesHint(activityTypes);
  const canAssignTerritory = can("crm.territories");
  const canEditCompany = can("crm.companies", "update");
  const canUseAgreements = can("crm.agreements");
  const agreementsAvailable = canUseAgreements && typeof crmApi.listAgreements === "function";
  const dealsAvailable = can("crm.opportunities") && typeof crmApi.listOpportunities === "function";
  const quotesAvailable = can("crm.quotes") && typeof crmApi.listQuotes === "function";
  const ordersAvailable = can("crm.orders") && typeof crmApi.listOrders === "function";
  const invoicesAvailable = can("crm.invoices") && typeof opsApi.listInvoices === "function";
  const [tab, setTab] = useState<Tab>("details");
  const [company, setCompany] = useState<Company | null>(null);
  const [dashboard, setDashboard] = useState<CompanyDashboard | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [agreements, setAgreements] = useState<CommercialAgreement[]>([]);
  const [deals, setDeals] = useState<Opportunity[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY);
  const [sourceId, setSourceId] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [contactFilter, setContactFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const editingContact = contacts.find((person) => person.id === editingContactId) ?? null;
  const [viewingContactId, setViewingContactId] = useState<string | null>(null);
  const viewingContact = contacts.find((person) => person.id === viewingContactId) ?? null;
  const contactDuplicates = useContactDuplicates(
    orgId,
    {
      company_id: companyId ?? "",
      email: contactForm.work_email,
      first_name: contactForm.first_name,
      last_name: contactForm.last_name,
      mobile: contactForm.mobile,
    },
    editingContactId,
  );
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  useEffect(() => {
    setActivityForm((current) => {
      if (activityTypes.some((item) => item.value === current.activity_type)) return current;
      if (editingActivityId) return current;
      return { ...current, activity_type: activityTypes[0]?.value ?? "cold_call" };
    });
  }, [activityTypes, editingActivityId]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const activityCols = useColumnResize();
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showAgreementForm, setShowAgreementForm] = useState(false);
  const [agreementForm, setAgreementForm] = useState(EMPTY_AGREEMENT);
  const [leadStatusFilter, setLeadStatusFilter] = useState("");
  const [recordLayout, setRecordLayout] = useState<AppLayout | null>(null);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [statusFieldOptions, setStatusFieldOptions] = useState<unknown>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // Loxley is offered the contact being viewed, else the company.
  useLoxleyOffer(
    viewingContact
      ? { kind: "contact", id: viewingContact.id, label: contactName(viewingContact) }
      : company ? { kind: "company", id: company.id, label: company.legal_name } : null,
  );

  const openContactEdit = (person: Contact) => {
    setViewingContactId(null);
    setEditingContactId(person.id);
    setShowContactForm(true);
    setContactForm({
      first_name: person.first_name,
      last_name: person.last_name ?? "",
      work_email: person.work_email ?? person.email ?? "",
      personal_email: person.personal_email ?? "",
      mobile: person.mobile ?? "",
      marketing_enabled: Boolean(person.marketing_enabled),
      is_active: person.is_active !== false,
    });
  };

  const openContactView = (person: Contact) => {
    setShowContactForm(false);
    setEditingContactId(null);
    setContactForm(EMPTY_CONTACT);
    setViewingContactId(person.id);
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [c, d, t, people, logs, companyLeads, designed, territoryRows, agreementRows, productRows, fieldRows, dealRows, quoteRows, orderRows, invoiceRows] = await Promise.all([
        crmApi.getCompany(companyId, orgId),
        crmApi.getDashboard(companyId, orgId).catch(() => null),
        crmApi.getTimeline(companyId, orgId).catch(() => []),
        crmApi.listContacts(orgId, companyId),
        crmApi.listActivities(orgId, { companyId }),
        crmApi.listLeads(orgId, undefined, undefined, companyId),
        opsApi.getLayout(orgId, "company").then(withNormalizedLayout).catch(() => null),
        opsApi.listTerritories(orgId).then((row) => row.territories).catch(() => [] as Territory[]),
        agreementsAvailable ? crmApi.listAgreements(orgId, companyId).catch(() => [] as CommercialAgreement[]) : Promise.resolve([]),
        agreementsAvailable && typeof crmApi.listProducts === "function" ? crmApi.listProducts(orgId).catch(() => [] as Product[]) : Promise.resolve([]),
        typeof opsApi.listFields === "function"
          ? opsApi.listFields(orgId, "company").catch(() => null)
          : Promise.resolve(null),
        dealsAvailable
          ? crmApi.listOpportunities(orgId, undefined, undefined, pickerPage(), { companyId }).catch(() => [] as Opportunity[])
          : Promise.resolve([] as Opportunity[]),
        quotesAvailable
          ? crmApi.listQuotes(orgId, undefined, undefined, undefined, pickerPage(), { companyId }).catch(() => [] as Quote[])
          : Promise.resolve([] as Quote[]),
        ordersAvailable
          ? crmApi.listOrders(orgId, undefined, undefined, pickerPage(), { companyId }).catch(() => [] as Order[])
          : Promise.resolve([] as Order[]),
        invoicesAvailable
          ? opsApi.listInvoices(orgId, undefined, pickerPage(), companyId).then((row) => row.invoices).catch(() => [] as Invoice[])
          : Promise.resolve([] as Invoice[]),
      ]);
      setCompany(c);
      setDashboard(d);
      setTimeline(t);
      setContacts(people);
      setActivities(logs);
      setLeads(companyLeads);
      setAgreements(agreementRows);
      setDeals(dealRows);
      setQuotes(quoteRows);
      setOrders(orderRows);
      setInvoices(invoiceRows);
      setProducts(productRows);
      setRecordLayout(designed);
      setTerritories(territoryRows);
      const statusField = fieldRows?.fields?.find((field) => field.field_key === "account_status");
      setStatusFieldOptions(statusField?.options ?? null);
      const fallbackTerritory = territoryRows.find((item) => item.code === "global") ?? territoryRows[0];
      setForm(formFromCompany(c, fallbackTerritory?.id ?? ""));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load company");
    } finally {
      setLoading(false);
    }
  }, [companyId, orgId, agreementsAvailable, dealsAvailable, quotesAvailable, ordersAvailable, invoicesAvailable]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get("edit") !== "1") return;
    if (!can("crm.companies", "update")) return;
    setTab("details");
    setEditing(true);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("edit");
      return next;
    }, { replace: true });
  }, [searchParams, can, setSearchParams]);

  useEffect(() => {
    const targetTab = searchParams.get("tab");
    if (targetTab !== "activities") return;
    setTab("activities");
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("tab");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const targetContactId = searchParams.get("contact");
    if (!targetContactId || contacts.length === 0) return;
    const person = contacts.find((item) => item.id === targetContactId);
    if (!person) return;
    setTab("contacts");
    // A link to a contact opens it to read; editing is a deliberate click on Edit.
    setShowContactForm(false);
    setEditingContactId(null);
    setViewingContactId(person.id);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("contact");
      return next;
    }, { replace: true });
  }, [searchParams, contacts, setSearchParams]);

  const commercialTabItems = (withCounts: boolean) => [
    ...(dealsAvailable ? [{ key: "deals", label: withCounts ? `Deals (${deals.length})` : "Deals" }] : []),
    ...(quotesAvailable ? [{ key: "quotes", label: withCounts ? `Quotes (${quotes.length})` : "Quotes" }] : []),
    ...(ordersAvailable ? [{ key: "orders", label: withCounts ? `Orders (${orders.length})` : "Orders" }] : []),
    ...(invoicesAvailable ? [{ key: "invoices", label: withCounts ? `Invoices (${invoices.length})` : "Invoices" }] : []),
  ];

  if (loading) {
    return (
      <RecordWorkspace
        kind="company"
        objectLabel="Company"
        title="Loading…"
        backTo={crmHref("/companies")}
        backLabel="← Back to Companies"
        highlights={[]}
        actions={
          <Button data-tour="new-lead" onClick={() => { setTab("leads"); setShowLeadForm(true); }}>New lead</Button>
        }
        tabs={[
          { key: "details", label: "Details" },
          { key: "contacts", label: "Contacts" },
          { key: "activities", label: "Activities" },
          { key: "leads", label: "Leads" },
          ...commercialTabItems(false),
          ...(agreementsAvailable ? [{ key: "agreements", label: "Agreements" }] : []),
        ]}
        activeTab={tab}
        onTabChange={(key) => setTab(key as Tab)}
      >
        <Card>Loading...</Card>
      </RecordWorkspace>
    );
  }
  if (error) return <Card className="crm-error">{error}</Card>;
  if (!company || !companyId) return <Card>Company not found</Card>;

  const kpi = dashboard?.kpi;
  const billing = formatAddress(company);
  const contactQuery = contactFilter.trim().toLowerCase();
  const filteredContacts = contactQuery
    ? contacts.filter((person) => {
        const haystack = [
          person.first_name,
          person.last_name,
          person.work_email,
          person.personal_email,
          person.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(contactQuery);
      })
    : contacts;
  const activityQuery = activityFilter.trim().toLowerCase();
  const filteredTimeline = activityQuery
    ? timeline.filter((item) => {
        const haystack = [
          item.event_type,
          timelineTaskType(item),
          timelineTouchPoint(item),
          item.title,
          item.description,
          timelineBefore(item),
          timelineAfter(item),
          item.actor_name,
          item.actor_user_id,
          item.source,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(activityQuery);
      })
    : timeline;
  const filteredLeads = leadStatusFilter
    ? leads.filter((lead) => (lead.lead_status ?? "new") === leadStatusFilter)
    : leads;
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));
  const leadStatusText = (lead: Lead) => {
    const converted = Boolean(lead.converted_at) || lead.lead_status === "converted";
    return converted && (lead.opportunity_stage_label || lead.opportunity_stage)
      ? `Lead convert: Opportunity stage ${lead.opportunity_stage_label || lead.opportunity_stage}`
      : (lead.lead_status ?? "new");
  };
  const distinct = (values: string[]) => Array.from(new Set(values)).sort();
  const companyLeadColumns: DataColumn[] = [
    {
      key: "lead",
      label: "Lead",
      primary: true,
      sortable: true,
      sortValue: (row) => {
        const lead = leadById.get(row.id);
        return lead ? leadDisplayName(lead) : "";
      },
    },
    {
      key: "source",
      label: "Source",
      sortable: true,
      sortValue: (row) => leadById.get(row.id)?.lead_source ?? "",
      filterOptions: distinct(leads.map((lead) => lead.lead_source ?? "").filter(Boolean)),
    },
    {
      key: "temperature",
      label: "Temperature",
      sortable: true,
      sortValue: (row) => leadById.get(row.id)?.rating ?? "cold",
      filterOptions: distinct(leads.map((lead) => lead.rating ?? "cold")),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortValue: (row) => {
        const lead = leadById.get(row.id);
        return lead ? leadStatusText(lead) : "";
      },
      filterOptions: distinct(leads.map(leadStatusText)),
    },
    {
      key: "created",
      label: "Created",
      sortable: true,
      sortValue: (row) => leadById.get(row.id)?.created_at ?? "",
    },
  ];
  const openLeadCount = leads.filter((lead) => !lead.converted_at).length;
  const fieldControl = (fieldKey: string, label: string, formatted: string, field?: FieldDef) => (
    <CompanyFieldControl
      fieldKey={fieldKey}
      label={label}
      field={field}
      formatted={formatted}
      form={form}
      setForm={setForm}
      territories={territories}
      canAssignTerritory={canAssignTerritory}
    />
  );
  const shown = (value?: string | null) => value || "—";

  function startEditCompany() {
    setEditing(true);
  }

  function cancelEditCompany() {
    if (!company) return;
    const fallbackTerritory = territories.find((item) => item.code === "global") ?? territories[0];
    setForm(formFromCompany(company, fallbackTerritory?.id ?? ""));
    setEditing(false);
  }

  function saveCompany() {
    if (!companyId) return;
    setError(null);
    void crmApi
      .updateCompany(companyId, companyPatch(form, canAssignTerritory), orgId)
      .then(() => {
        setEditing(false);
        show("Saved!", "success");
        return load();
      })
      .catch((e) => setError(reportSaveFailure(show, e, "Update failed")));
  }

  async function changeStatus(next: string) {
    if (!companyId || !company || next === (company.account_status ?? "") || statusSaving) return;
    setStatusSaving(true);
    setError(null);
    try {
      await crmApi.updateCompany(companyId, { account_status: next }, orgId);
      setCompany({ ...company, account_status: next });
      setForm((current) => ({ ...current, account_status: next }));
      show("Status updated", "success");
    } catch (e) {
      setError(reportSaveFailure(show, e, "Status could not be changed."));
    } finally {
      setStatusSaving(false);
    }
  }

  function addAgreementLine() {
    const product = products[0];
    if (!product) return;
    setAgreementForm((current) => ({
      ...current,
      lines: [
        ...current.lines,
        {
          product_id: product.id,
          contracted_unit_price: product.unit_price ?? null,
          unit_of_measure: product.unit_of_measure ?? "each",
          min_qty: 1,
          max_qty: null,
          discount_pct: null,
        },
      ],
    }));
  }

  function saveAgreement() {
    if (!companyId) return;
    void crmApi
      .createAgreement(
        {
          company_id: companyId,
          territory_id: canAssignTerritory ? form.territory_id || company?.territory_id || null : company?.territory_id ?? null,
          status: agreementForm.status,
          start_date: agreementForm.start_date || null,
          end_date: agreementForm.end_date || null,
          payment_terms: agreementForm.payment_terms || null,
          delivery_terms: agreementForm.delivery_terms || null,
          notes: agreementForm.notes || null,
          lines: agreementForm.lines,
        },
        orgId,
      )
      .then(() => {
        setShowAgreementForm(false);
        setAgreementForm(EMPTY_AGREEMENT);
        show("Saved!", "success");
        return load();
      })
      .catch((e) => setError(reportSaveFailure(show, e, "Failed to save agreement")));
  }

  return (
    <RecordWorkspace
      kind="company"
      objectLabel="Company"
      title={company.legal_name}
      subtitle={company.trading_name || undefined}
      backTo={crmHref("/companies")}
      backLabel="← Back to Companies"
      highlights={[
        { label: "Billing address", value: billing },
        {
          label: "Status",
          hint: canEditCompany && company.account_status !== "merged"
            ? "Where this account is in its lifecycle. Click the badge to change it."
            : "Where this account is in its lifecycle.",
          value: company.account_status
            ? (canEditCompany && company.account_status !== "merged"
              ? (
                <select
                  className={`crm-badge crm-status-picker crm-badge--${company.account_status}`}
                  aria-label="Change company status"
                  title={statusTitle(COMPANY_STATUS_HINTS, company.account_status)}
                  value={company.account_status}
                  disabled={statusSaving}
                  onChange={(event) => void changeStatus(event.target.value)}
                >
                  {companyStatusChoices(statusFieldOptions, company.account_status).map((option) => (
                    <option key={option} value={option}>
                      {option === "churned" ? "Inactive" : statusText(option, COMPANY_STATUS_HINTS)}
                    </option>
                  ))}
                </select>
              )
              : <StatusBadge status={company.account_status} options={COMPANY_STATUS_HINTS} />)
            : undefined,
        },
        { label: "Company code", value: company.company_code },
        {
          label: "Salesperson",
          value: (
            <RecordOwner
              orgId={orgId}
              kind="companies"
              recordId={company.id}
              ownerUserId={company.owner_user_id}
              ownerName={company.owner_name}
              canReassign={company.can_reassign_owner}
              onChanged={(owner) => setCompany((prev) => (prev ? { ...prev, ...owner } : prev))}
            />
          ),
        },
        { label: "Industry", value: company.industry },
        { label: "Phone", value: company.primary_phone },
        { label: "Email", value: company.primary_email },
        {
          label: "Territory",
          value: (
            <span className="crm-badge crm-badge--territory">{company.territory_name ?? "Global"}</span>
          ),
        },
      ]}
      actions={
        <ResponsiveActions
          primary={<Button data-tour="new-lead" onClick={() => { setTab("leads"); setShowLeadForm(true); }}>New lead</Button>}
          more={
            <>
              {can("crm.support", "create") && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (!companyId) return;
                    navigate(
                      crmHref(
                        `/support?companyId=${encodeURIComponent(companyId)}&companyName=${encodeURIComponent(company.legal_name)}&new=1`,
                      ),
                    );
                  }}
                >
                  New support ticket
                </Button>
              )}
              {can("crm.sales_cycle", "create") && companyId && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    void opsApi.startCycle(orgId, companyId).then((run) => navigate(crmHref(`/sales-cycle/${run.id}`)));
                  }}
                >
                  Start sales cycle
                </Button>
              )}
              <CompanyDeleteButton
                orgId={orgId}
                companyId={companyId}
                recordLabel={company.legal_name}
                onDeleted={() => navigate(crmHref("/companies"))}
              />
            </>
          }
        />
      }
      tabs={[
        { key: "details", label: "Details" },
        { key: "contacts", label: "Contacts" },
        { key: "activities", label: "Activities" },
        { key: "leads", label: "Leads" },
        ...commercialTabItems(true),
        ...(agreementsAvailable ? [{ key: "agreements", label: `Agreements (${agreements.length})` }] : []),
      ]}
      activeTab={tab}
      onTabChange={(key) => setTab(key as Tab)}
      aside={
        <>
          <div className="crm-aside-panel">
            <h2 className="crm-aside-title">Related quick links</h2>
            <div className="crm-quick-links">
              <button type="button" className="crm-quick-link" onClick={() => setTab("contacts")}>
                <strong>{contacts.length}</strong>
                Contacts
              </button>
              <button type="button" className="crm-quick-link" onClick={() => setTab("leads")}>
                <strong>{openLeadCount}</strong>
                Open leads
              </button>
              <button type="button" className="crm-quick-link" onClick={() => setTab("activities")}>
                <strong>{timeline.length}</strong>
                Activities
              </button>
              <div className="crm-quick-link">
                <strong>{kpi?.activities_30d ?? 0}</strong>
                Last 30 days
              </div>
            </div>
          </div>

          {kpi && can("crm.opportunities") && (
            dealsAvailable ? (
              <button type="button" className="crm-aside-panel" onClick={() => setTab("deals")}>
                <h2 className="crm-aside-title">Pipeline snapshot</h2>
                <div className="crm-kpi-value">{formatMoney(kpi.open_pipeline_value, undefined, true)}</div>
                <div className="crm-kpi-label">Open pipeline ({kpi.open_pipeline_count})</div>
              </button>
            ) : (
              <div className="crm-aside-panel">
                <h2 className="crm-aside-title">Pipeline snapshot</h2>
                <div className="crm-kpi-value">{formatMoney(kpi.open_pipeline_value, undefined, true)}</div>
                <div className="crm-kpi-label">Open pipeline ({kpi.open_pipeline_count})</div>
              </div>
            )
          )}

          <div className="crm-aside-panel">
            <h2 className="crm-aside-title">Upcoming & activity</h2>
            {timeline.length === 0 ? (
              <EmptyState title="No events yet" description="Logged calls, tickets, quotes, and cycle updates appear here." />
            ) : (
              <div className="crm-timeline">
                {timeline.slice(0, 8).map((ev, i) => {
                  const link = recordLink(ev.related_record_type, ev.related_record_id);
                  const title = ev.title || timelineTypeLabel(ev);
                  return (
                    <div key={`${ev.record_id}-${i}`} className="crm-timeline-item">
                      <div className="crm-timeline-time">{ev.sort_at ? formatDate(ev.sort_at) : ""}</div>
                      <div className="crm-timeline-title">
                        {link && can(link.capability) ? (
                          <Link to={crmHref(link.path)}>{title}</Link>
                        ) : ev.source === "activity" ? (
                          <button type="button" className="crm-timeline-title" onClick={() => setTab("activities")}>
                            {title}
                          </button>
                        ) : (
                          title
                        )}
                      </div>
                      {ev.description && <div className="crm-timeline-desc">{ev.description}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      }
    >
      {tab === "details" && (
        <>
          {canEditCompany ? (
            <div className="crm-toolbar crm-toolbar--end">
              {editing ? (
                <Cluster gap={2}>
                  <Button onClick={() => saveCompany()} disabled={!form.legal_name?.trim()}>Save</Button>
                  <Button variant="secondary" onClick={cancelEditCompany}>Cancel</Button>
                </Cluster>
              ) : (
                <Button onClick={startEditCompany}>Edit</Button>
              )}
            </div>
          ) : null}
          {recordLayout ? (
            <DesignedRecordFields
              layout={recordLayout}
              record={company as unknown as Record<string, unknown>}
              renderValue={
                editing
                  ? ({ fieldKey, formatted, field }) => fieldControl(fieldKey, field?.label ?? fieldKey, formatted, field)
                  : undefined
              }
            />
          ) : (
            <>
              <DetailSection title="Company information">
                <FieldItem label="Legal name" hint={FIELD_HINTS.company_legal_name} value={editing ? fieldControl("legal_name", "Legal name", shown(company.legal_name)) : company.legal_name} />
                <FieldItem label="Trading name" hint={FIELD_HINTS.company_trading_name} value={editing ? fieldControl("trading_name", "Trading name", shown(company.trading_name)) : company.trading_name} />
                <FieldItem label="Company code" hint={FIELD_HINTS.company_code} value={company.company_code} />
                <FieldItem label="Status" hint={FIELD_HINTS.company_status} value={editing ? fieldControl("account_status", "Status", shown(company.account_status)) : company.account_status} />
                <FieldItem label="Industry" hint={FIELD_HINTS.company_industry} value={editing ? fieldControl("industry", "Industry", shown(company.industry)) : company.industry} />
                <FieldItem label="Website" hint={FIELD_HINTS.company_website} value={editing ? fieldControl("website", "Website", shown(company.website)) : company.website} />
                <FieldItem label="Phone" hint={FIELD_HINTS.company_phone} value={editing ? fieldControl("primary_phone", "Phone", shown(company.primary_phone)) : company.primary_phone} />
                <FieldItem label="Email" hint={FIELD_HINTS.company_email} value={editing ? fieldControl("primary_email", "Email", shown(company.primary_email)) : company.primary_email} />
                <FieldItem
                  label="Territory"
                  hint="Sales territory this account belongs to — used for ownership and reporting."
                  value={editing ? fieldControl("territory_id", "Territory", company.territory_name ?? "Global") : (company.territory_name ?? "Global")}
                />
              </DetailSection>
              <DetailSection title="Registration & tax">
                <FieldItem label="Registration" hint={FIELD_HINTS.company_registration} value={editing ? fieldControl("registration_number", "Registration", shown(company.registration_number)) : company.registration_number} />
                <FieldItem label="Tax / VAT" hint={FIELD_HINTS.company_vat} value={editing ? fieldControl("vat_number", "Tax / VAT", shown(company.vat_number)) : company.vat_number} />
                <FieldItem label="Currency" hint={FIELD_HINTS.company_currency} value={editing ? fieldControl("currency", "Currency", shown(company.currency)) : company.currency} />
                <FieldItem label="Payment terms" hint={FIELD_HINTS.company_payment_terms} value={editing ? fieldControl("payment_terms", "Payment terms", shown(company.payment_terms)) : company.payment_terms} />
              </DetailSection>
              <DetailSection title="Billing address">
                <FieldItem label="Address" hint={FIELD_HINTS.company_address} value={editing ? fieldControl("billing_address_line1", "Address", shown(company.billing_address_line1)) : billing} />
                <FieldItem label="City" hint={FIELD_HINTS.company_city} value={editing ? fieldControl("billing_address_city", "City", shown(company.billing_address_city)) : company.billing_address_city} />
                <FieldItem label="State / region" hint={FIELD_HINTS.company_state} value={editing ? fieldControl("billing_address_state", "State / region", shown(company.billing_address_state)) : company.billing_address_state} />
                <FieldItem label="Country" hint={FIELD_HINTS.company_country} value={editing ? fieldControl("billing_address_country", "Country", shown(company.billing_address_country)) : company.billing_address_country} />
              </DetailSection>
              {company.notes || editing ? (
                <DetailSection title="Additional information">
                  <FieldItem label="Notes" hint={FIELD_HINTS.company_notes} value={editing ? fieldControl("notes", "Notes", shown(company.notes)) : company.notes} />
                </DetailSection>
              ) : null}
              <RecordAuditInfo record={company} />
            </>
          )}

          {can("crm.companies", "merge") && (
            <div className="crm-panel-card">
              <Stack gap={2}>
                <h3 className="crm-section-title">Merge another company into this one</h3>
                <p className="crm-help">Sales admin only. Search for the company to absorb into this record. Its contacts, leads, and history move here.</p>
                <CompanyLookup
                  orgId={orgId}
                  value={sourceName}
                  companyId={sourceId}
                  allowCreate={false}
                  excludeId={companyId}
                  label="Company to merge"
                  onChange={({ name, companyId: nextId }) => {
                    setSourceName(name);
                    setSourceId(nextId);
                  }}
                />
                <Button
                  variant="secondary"
                  disabled={!sourceId.trim() || sourceId === companyId}
                  onClick={() =>
                    void crmApi
                      .mergeCompanies(companyId, sourceId.trim(), orgId)
                      .then(() => {
                        setSourceId("");
                        setSourceName("");
                        show("Saved!", "success");
                        return load();
                      })
                      .catch((e) => setError(reportSaveFailure(show, e, "Merge failed")))
                  }
                >
                  Merge
                </Button>
              </Stack>
            </div>
          )}
        </>
      )}

      {tab === "contacts" && (
        <>
          <div className="crm-toolbar crm-toolbar--split">
            <SearchBar
              value={contactFilter}
              onChange={setContactFilter}
              placeholder="Search contacts…"
              label="Search contacts"
              hint="Search by name or email."
            />
            <Button
              data-tour="add-contact"
              onClick={() => {
                if (showContactForm) {
                  setShowContactForm(false);
                  setEditingContactId(null);
                  setContactForm(EMPTY_CONTACT);
                  return;
                }
                setEditingContactId(null);
                setViewingContactId(null);
                setContactForm(EMPTY_CONTACT);
                setShowContactForm(true);
              }}
            >
              {showContactForm ? "Cancel" : "New contact"}
            </Button>
          </div>
          {viewingContact && !showContactForm ? (
            <ContactViewCard
              person={viewingContact}
              onEdit={() => openContactEdit(viewingContact)}
              onClose={() => setViewingContactId(null)}
            />
          ) : null}
          {showContactForm && (
            <Card>
              <Stack gap={3}>
                <h2 className="crm-section-title">{editingContactId ? "Edit contact" : "Add contact"}</h2>
                <Input label="First name" value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} />
                <Input label="Last name" value={contactForm.last_name} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} />
                <Input label="Work email" value={contactForm.work_email} onChange={(e) => setContactForm({ ...contactForm, work_email: e.target.value })} />
                <Input label="Personal email" value={contactForm.personal_email} onChange={(e) => setContactForm({ ...contactForm, personal_email: e.target.value })} />
                <Input label="Mobile" value={contactForm.mobile} onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })} />
                <ContactDuplicateWarning duplicates={contactDuplicates} />
                <label className="crm-check">
                  <input
                    type="checkbox"
                    checked={contactForm.marketing_enabled}
                    onChange={(e) => setContactForm({ ...contactForm, marketing_enabled: e.target.checked })}
                  />
                  Marketing enabled
                </label>
                <label className="crm-check">
                  <input
                    type="checkbox"
                    checked={contactForm.is_active}
                    onChange={(e) => setContactForm({ ...contactForm, is_active: e.target.checked })}
                  />
                  Active at this company
                </label>
                {editingContact ? <RecordAuditInfo record={editingContact} /> : null}
                <div className="crm-form-actions">
                  <Button
                    disabled={!contactForm.first_name.trim()}
                    onClick={() => {
                      const body = {
                        company_id: companyId,
                        first_name: contactForm.first_name,
                        last_name: contactForm.last_name || null,
                        work_email: contactForm.work_email || null,
                        personal_email: contactForm.personal_email || null,
                        mobile: contactForm.mobile || null,
                        marketing_enabled: contactForm.marketing_enabled,
                        is_active: contactForm.is_active,
                      };
                      const request = editingContactId
                        ? crmApi.updateContact(editingContactId, body, orgId)
                        : crmApi.createContact(body, orgId);
                      void request
                        .then(() => {
                          setShowContactForm(false);
                          setEditingContactId(null);
                          setContactForm(EMPTY_CONTACT);
                          show("Saved!", "success");
                          return load();
                        })
                        .catch((e) => setError(reportSaveFailure(show, e, "Failed to save contact")));
                    }}
                  >
                    {editingContactId ? "Save contact" : "Create contact"}
                  </Button>
                </div>
              </Stack>
            </Card>
          )}
          <Card className="crm-table-card">
            {contacts.length === 0 && !showContactForm ? (
              <EmptyState
                title="No contacts"
                description="Add the people who work at this company."
                action={<Button onClick={() => { setEditingContactId(null); setContactForm(EMPTY_CONTACT); setShowContactForm(true); }}>New contact</Button>}
              />
            ) : contacts.length === 0 ? null : filteredContacts.length === 0 ? (
              <EmptyState title="No matches" description="Try a different name or email filter." />
            ) : (
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Work email</th>
                    <th>Marketing</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((person) => (
                    <tr key={person.id}>
                      <td>
                        {person.first_name} {person.last_name}
                        {person.is_primary && <span className="crm-badge crm-badge--active">Primary</span>}
                      </td>
                      <td>{person.work_email ?? person.email ?? "—"}</td>
                      <td>{person.marketing_enabled ? "Yes" : "No"}</td>
                      <td>
                        <span className={`crm-badge ${person.is_active === false ? "crm-badge--lost" : "crm-badge--active"}`}>
                          {person.is_active === false ? "Inactive" : "Active"}
                        </span>
                      </td>
                      <td>
                        <Cluster gap={2}>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`View ${contactName(person)}`}
                            title="Open this contact to read its details. Nothing can change until you click Edit."
                            onClick={() => openContactView(person)}
                          >
                            View
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openContactEdit(person)}>
                            Edit
                          </Button>
                          <DeleteRecordButton
                            capability="crm.contacts"
                            recordLabel={contactName(person)}
                            size="sm"
                            variant="ghost"
                            onDeleted={async () => {
                              await crmApi.deleteContact(person.id, orgId);
                              if (viewingContactId === person.id) setViewingContactId(null);
                              await load();
                            }}
                          />
                        </Cluster>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      {tab === "activities" && (
        <>
          <div className="crm-toolbar crm-toolbar--split">
            <SearchBar
              value={activityFilter}
              onChange={setActivityFilter}
              placeholder="Search activities…"
              label="Search activities"
              hint="Search by type, subject, ticket, quote, or outcome."
            />
            <Button
              onClick={() => {
                if (showActivityForm) {
                  setShowActivityForm(false);
                  setEditingActivityId(null);
                  setActivityForm(EMPTY_ACTIVITY);
                  return;
                }
                setEditingActivityId(null);
                setActivityForm(EMPTY_ACTIVITY);
                setShowActivityForm(true);
              }}
            >
              {showActivityForm ? "Cancel" : "Log activity"}
            </Button>
          </div>
          {showActivityForm && (
            <Card>
              <Stack gap={3}>
                <h2 className="crm-section-title">{editingActivityId ? "Edit activity" : "Log activity"}</h2>
                <FieldLabel htmlFor="activity-type" hint={activityHint}>
                  Type
                </FieldLabel>
                <select
                  id="activity-type"
                  className="crm-select"
                  value={activityForm.activity_type}
                  onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                >
                  {activityForm.activity_type && !activityTypes.some((item) => item.value === activityForm.activity_type) && (
                    <option value={activityForm.activity_type}>
                      {labelForActivityType(activityForm.activity_type, activityTypes)}
                    </option>
                  )}
                  {activityTypes.map((item) => (
                    <option key={item.value} value={item.value} title={item.meaning}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <p className="home-subtitle">
                  Types that count toward cold calls, emails, meetings, or demos update the owner's sales plan.
                  A type set to Does not count, such as a note, does not.
                </p>
                <FieldLabel htmlFor="activity-contact" hint="Person this was with. The company still receives the activity if you leave this blank.">
                  Contact
                </FieldLabel>
                <select
                  id="activity-contact"
                  className="crm-select"
                  value={activityForm.contact_id}
                  onChange={(e) => setActivityForm({ ...activityForm, contact_id: e.target.value })}
                >
                  <option value="">Company only</option>
                  {contacts.map((person) => (
                    <option key={person.id} value={person.id}>{contactName(person)}</option>
                  ))}
                </select>
                <Input
                  label="Subject"
                  tooltip={FIELD_HINTS.activity_subject}
                  value={activityForm.subject}
                  onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })}
                />
                <label className="crm-field">
                  <span>Details / comments</span>
                  <textarea
                    className="crm-textarea crm-textarea--activity"
                    value={activityForm.body}
                    onChange={(e) => setActivityForm({ ...activityForm, body: e.target.value })}
                    rows={6}
                  />
                </label>
                <FieldLabel htmlFor="activity-outcome" hint={FIELD_HINTS.activity_outcome}>
                  Outcome
                </FieldLabel>
                <select
                  id="activity-outcome"
                  className="crm-select"
                  value={activityForm.outcome}
                  onChange={(e) => setActivityForm({ ...activityForm, outcome: e.target.value })}
                >
                  <option value="">No outcome</option>
                  {activityForm.outcome && !ACTIVITY_OUTCOMES.some((item) => item.value === activityForm.outcome) && (
                    <option value={activityForm.outcome}>{activityForm.outcome}</option>
                  )}
                  {ACTIVITY_OUTCOMES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <div className="crm-form-actions">
                  <Button
                    onClick={() => {
                      const body = {
                        company_id: companyId,
                        activity_type: activityForm.activity_type,
                        contact_id: activityForm.contact_id || null,
                        subject: activityForm.subject || null,
                        body: activityForm.body || null,
                        outcome: activityForm.outcome || null,
                        ...(editingActivityId ? {} : { occurred_at: new Date().toISOString() }),
                      };
                      const request = editingActivityId
                        ? crmApi.updateActivity(editingActivityId, body, orgId)
                        : crmApi.createActivity(body, orgId);
                      void request
                        .then(() => {
                          setShowActivityForm(false);
                          setEditingActivityId(null);
                          setActivityForm(EMPTY_ACTIVITY);
                          show("Saved!", "success");
                          return load();
                        })
                        .catch((e) => setError(reportSaveFailure(show, e, "Failed to save activity")));
                    }}
                  >
                    {editingActivityId ? "Save activity" : "Log activity"}
                  </Button>
                </div>
              </Stack>
            </Card>
          )}
          <Card className="crm-table-card">
            {timeline.length === 0 && !showActivityForm ? (
              <EmptyState
                title="No activities yet"
                description="Calls, meetings, tickets, quotes, orders, invoices, and sales-cycle updates for this company belong here."
                action={<Button onClick={() => { setEditingActivityId(null); setActivityForm(EMPTY_ACTIVITY); setShowActivityForm(true); }}>Log activity</Button>}
              />
            ) : timeline.length === 0 ? null : filteredTimeline.length === 0 ? (
              <EmptyState title="No matches" description="Try a different type, subject, or outcome." />
            ) : (
              <table {...activityCols.tableProps}>
                <thead>
                  <tr ref={activityCols.headRef}>
                    {["Task type", "Touch point", "Subject", "Detail", "Before", "After", "User", "When", ""].map(
                      (label, i) => (
                        <th key={label || "actions"} {...activityCols.thProps(i)}>
                          {label}
                          {label ? activityCols.handle(i) : null}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredTimeline.map((item) => {
                    const editable = isEditableActivity(item);
                    const logged = editable
                      ? activities.find((row) => row.id === item.record_id)
                      : undefined;
                    return (
                      <tr key={`${item.source}-${item.record_id}`}>
                        <td>{timelineTaskType(item)}</td>
                        <td>{timelineTouchPoint(item)}</td>
                        <td>
                          {(() => {
                            const link = recordLink(item.related_record_type, item.related_record_id);
                            const title = item.title ?? "—";
                            return link && can(link.capability) ? <Link to={crmHref(link.path)}>{title}</Link> : title;
                          })()}
                        </td>
                        <td>{item.description || "—"}</td>
                        <td>{timelineBefore(item) || "—"}</td>
                        <td>{timelineAfter(item) || "—"}</td>
                        <td>{item.actor_name || "—"}</td>
                        <td>{item.sort_at ? formatDate(item.sort_at) : "—"}</td>
                        <td>
                          {editable ? (
                            <Cluster gap={2}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingActivityId(item.record_id);
                                  setShowActivityForm(true);
                                  setActivityForm({
                                    activity_type: logged?.activity_type ?? item.event_type ?? "cold_call",
                                    subject: logged?.subject ?? item.title ?? "",
                                    body: logged?.body ?? "",
                                    outcome: logged?.outcome ?? "",
                                    contact_id: logged?.contact_id ?? "",
                                  });
                                }}
                              >
                                Edit
                              </Button>
                              <DeleteRecordButton
                                capability="crm.activities"
                                recordLabel={item.title || "this activity"}
                                size="sm"
                                variant="ghost"
                                onDeleted={async () => {
                                  await crmApi.deleteActivity(item.record_id, orgId);
                                  await load();
                                }}
                              />
                            </Cluster>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      {tab === "deals" && dealsAvailable && (
        <Card>
          {deals.length === 0 ? (
            <EmptyState title="No deals" description="Opportunities for this company show up here." />
          ) : (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Deal</th>
                  <th>Stage</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr key={deal.id}>
                    <td><Link to={crmHref(`/opportunities/${deal.id}`)}>{deal.title || deal.opportunity_code || "Deal"}</Link></td>
                    <td>{deal.stage ? statusLabel(deal.stage) : "—"}</td>
                    <td>{formatMoney(deal.value, deal.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "quotes" && quotesAvailable && (
        <Card>
          {quotes.length === 0 ? (
            <EmptyState title="No quotes" description="Quotes for this company show up here." />
          ) : (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Quote</th>
                  <th>Status</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id}>
                    <td><Link to={crmHref(`/quotes/${quote.id}`)}>{quote.quote_number || "Quote"}</Link></td>
                    <td>{quote.quote_status ? statusLabel(quote.quote_status) : "—"}</td>
                    <td>{formatMoney(quote.total, quote.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "orders" && ordersAvailable && (
        <Card>
          {orders.length === 0 ? (
            <EmptyState title="No orders" description="Orders for this company show up here." />
          ) : (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td><Link to={crmHref(`/orders/${order.id}`)}>{order.order_number || "Order"}</Link></td>
                    <td>{order.order_status ? statusLabel(order.order_status) : "—"}</td>
                    <td>{formatMoney(order.total, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "invoices" && invoicesAvailable && (
        <Card>
          {invoices.length === 0 ? (
            <EmptyState title="No invoices" description="Invoices for this company show up here." />
          ) : (
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Status</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td><Link to={crmHref(`/invoices/${invoice.id}`)}>{invoice.invoice_number || "Invoice"}</Link></td>
                    <td>{invoice.status ? statusLabel(invoice.status) : "—"}</td>
                    <td>{formatMoney(invoice.total, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "agreements" && agreementsAvailable && (
        <>
          <div className="crm-toolbar crm-toolbar--split">
            <p className="crm-help">Commercial agreements are the company price book for a territory. Lines reference the shared product catalogue.</p>
            <Button onClick={() => setShowAgreementForm((value) => !value)}>
              {showAgreementForm ? "Cancel" : "New agreement"}
            </Button>
          </div>
          {showAgreementForm && (
            <Card>
              <Stack gap={3}>
                <h2 className="crm-section-title">New commercial agreement</h2>
                <Select label="Status" value={agreementForm.status} onChange={(e) => setAgreementForm({ ...agreementForm, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
                <div className="crm-field-grid">
                  <Input label="Start date" type="date" value={agreementForm.start_date} onChange={(e) => setAgreementForm({ ...agreementForm, start_date: e.target.value })} />
                  <Input label="End date" type="date" value={agreementForm.end_date} onChange={(e) => setAgreementForm({ ...agreementForm, end_date: e.target.value })} />
                </div>
                <Input label="Payment terms" value={agreementForm.payment_terms} onChange={(e) => setAgreementForm({ ...agreementForm, payment_terms: e.target.value })} />
                <Input label="Delivery terms" value={agreementForm.delivery_terms} onChange={(e) => setAgreementForm({ ...agreementForm, delivery_terms: e.target.value })} />
                <DetailSection title="Agreement lines">
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Unit price</th>
                        <th>Discount %</th>
                        <th>UOM</th>
                        <th>Min</th>
                        <th>Max</th>
                        <th>Lead time</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {agreementForm.lines.map((line, index) => (
                        <tr key={index}>
                          <td>
                            <select
                              className="crm-select"
                              value={line.product_id}
                              onChange={(e) => {
                                const product = products.find((item) => item.id === e.target.value);
                                setAgreementForm((current) => ({
                                  ...current,
                                  lines: current.lines.map((row, rowIndex) =>
                                    rowIndex === index
                                      ? {
                                          ...row,
                                          product_id: e.target.value,
                                          contracted_unit_price: product?.unit_price ?? row.contracted_unit_price,
                                          unit_of_measure: product?.unit_of_measure ?? row.unit_of_measure ?? "each",
                                        }
                                      : row,
                                  ),
                                }));
                              }}
                            >
                              {products.map((product) => (
                                <option key={product.id} value={product.id}>{product.name}</option>
                              ))}
                            </select>
                          </td>
                          <td><Input type="number" value={String(line.contracted_unit_price ?? "")} onChange={(e) => setAgreementForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, contracted_unit_price: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                          <td><Input type="number" value={String(line.discount_pct ?? "")} onChange={(e) => setAgreementForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, discount_pct: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                          <td><Input value={line.unit_of_measure ?? "each"} onChange={(e) => setAgreementForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, unit_of_measure: e.target.value } : row) }))} /></td>
                          <td><Input type="number" value={String(line.min_qty ?? "")} onChange={(e) => setAgreementForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, min_qty: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                          <td><Input type="number" value={String(line.max_qty ?? "")} onChange={(e) => setAgreementForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, max_qty: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                          <td><Input type="number" value={String(line.lead_time_days ?? "")} onChange={(e) => setAgreementForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, lead_time_days: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                          <td><Button variant="ghost" size="sm" onClick={() => setAgreementForm((current) => ({ ...current, lines: current.lines.filter((_, rowIndex) => rowIndex !== index) }))}>Remove</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Cluster gap={2}>
                    <Button variant="secondary" onClick={addAgreementLine} disabled={products.length === 0}>Add line</Button>
                    <Button onClick={saveAgreement} disabled={agreementForm.lines.length === 0}>Save agreement</Button>
                  </Cluster>
                </DetailSection>
              </Stack>
            </Card>
          )}
          <Card className="crm-table-card">
            {agreements.length === 0 ? (
              <EmptyState title="No agreements" description="Create the customer-specific price book for this company." />
            ) : (
              <table className="crm-table">
                <thead>
                  <tr><th>Agreement</th><th>Status</th><th>Territory</th><th>Dates</th><th>Terms</th></tr>
                </thead>
                <tbody>
                  {agreements.map((agreement) => (
                    <tr key={agreement.id}>
                      <td><Link to={crmHref(`/agreements/${agreement.id}`)}>{agreement.agreement_number}</Link></td>
                      <td><StatusBadge status={agreement.status ?? "draft"} /></td>
                      <td>{agreement.territory_name ?? "Global"}</td>
                      <td>{[agreement.start_date, agreement.end_date].filter(Boolean).join(" - ") || "-"}</td>
                      <td>{agreement.payment_terms || agreement.delivery_terms || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      {tab === "leads" && (
        <>
          <div className="crm-toolbar crm-toolbar--split">
            <select
              className="crm-select"
              value={leadStatusFilter}
              onChange={(e) => setLeadStatusFilter(e.target.value)}
              aria-label="Filter leads by status"
            >
              <option value="">All leads ({leads.length})</option>
              <option value="new">New</option>
              <option value="working">Working</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="disqualified">Disqualified</option>
            </select>
            <Button onClick={() => setShowLeadForm((value) => !value)}>
              {showLeadForm ? "Hide form" : "New lead"}
            </Button>
          </div>

          {showLeadForm && (
            <div className="crm-panel-card">
              <h2 className="crm-section-title">New lead for {company.legal_name}</h2>
              <LeadCaptureForm
                orgId={orgId}
                lockedCompany={{ id: companyId, name: company.legal_name }}
                submitLabel="Add lead"
                onCreated={() => {
                  setShowLeadForm(false);
                  void load();
                }}
              />
            </div>
          )}

          <Card className="crm-table-card">
            {leads.length === 0 ? (
              <EmptyState
                title="No leads yet"
                description="A company can have many open leads — e.g. one per campaign response. Contact is optional."
                action={<Button onClick={() => setShowLeadForm(true)}>Add lead</Button>}
              />
            ) : filteredLeads.length === 0 ? (
              <EmptyState title="No leads in this status" description="Try another status filter." />
            ) : (
              <ResponsiveDataTable
                caption="Company leads"
                columns={companyLeadColumns}
                rows={filteredLeads.map((lead) => {
                  const converted = Boolean(lead.converted_at) || lead.lead_status === "converted";
                  const href =
                    converted && lead.converted_to_opportunity_id
                      ? crmHref(`/opportunities/${lead.converted_to_opportunity_id}`)
                      : crmHref(`/leads/${lead.id}`);
                  const statusLabel =
                    converted && (lead.opportunity_stage_label || lead.opportunity_stage)
                      ? `Lead convert: Opportunity stage ${lead.opportunity_stage_label || lead.opportunity_stage}`
                      : (lead.lead_status ?? "new");
                  return {
                    id: lead.id,
                    className: converted ? "is-pipeline-muted" : undefined,
                    cells: {
                      lead: (
                        <>
                          <Link to={href}>{leadDisplayName(lead)}</Link>
                          {lead.notes && <div className="crm-muted">{lead.notes}</div>}
                          {!lead.first_name && !lead.last_name && (
                            <div className="crm-muted">No contact named yet</div>
                          )}
                        </>
                      ),
                      source: lead.lead_source ?? "—",
                      temperature: <StatusBadge status={lead.rating ?? "cold"} options={LEAD_TEMPERATURE_OPTIONS} />,
                      status: <StatusBadge status={statusLabel} options={LEAD_STATUS_OPTIONS} />,
                      created: lead.created_at ? formatDate(lead.created_at) : "—",
                    },
                  };
                })}
              />
            )}
          </Card>
        </>
      )}
    </RecordWorkspace>
  );
}
